import React, { useEffect, useState } from 'react'
import FormControl from '@mui/material/FormControl'
import { TextField, Box, Grid, Link, MenuItem, Button as AscendButton } from '@achieve/ascend'
import Select from '@mui/material/Select'
import { Typography } from 'components/Contentful/Typography'
import { useViewportLargerThan, useViewportSmallerThan } from 'utils/viewport'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { useAppRouter } from 'hooks/useAppRouter/useAppRouter'
import { getBaseURL, getFilterUrlByValue } from 'utils/shared/filter'
import { AchieveLink } from 'components/AchieveLink'
import { unslug } from 'utils/routes'
import { handleTrackAndReactEvent } from 'utils/analytics'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import { mainProductConfig } from 'constants/mainProductConfig'
import classNames from 'classnames'
import { useSearchParams } from 'next/navigation'

function Filter({
  jsonContent,
  filterBy,
  withKeywords,
  withUrl,
  setFilterBy,
  setKeywords,
  config,
}) {
  const [keywordsValue, setKeywordsValue] = useState('')
  const { push, fullAsPath } = useAppRouter()
  const [paramsUtm, setParamsUtm] = useState({})
  const siteEnv = process.env.NEXT_PUBLIC_SITE_ENVIRONMENT || 'achieve'
  const { fireAndForget } = useTriggerEvents()
  const searchParams = useSearchParams()

  useEffect(() => {
    if (searchParams) {
      const params = searchParams
      let utmSource = {}
      for (const property in params) {
        if (property.includes('utm')) {
          utmSource[property] = params[property]
        }
      }
      setParamsUtm(utmSource)
    }
  }, [searchParams])

  const stringifyQueryParams = (params) => {
    let paramKeys = Object.keys(params)
    let queryString = '?'
    for (let i = 0; i < paramKeys.length; i++) {
      queryString = queryString + `${paramKeys[i]}=${params[paramKeys[i]]}`
    }
    return queryString
  }

  const handleChange = (event) => {
    const val = event.target.value

    let track = {
      list_name: 'Listing Page Category Filter',
      click_id: `Filter Topics - ${val}`,
      click_text: `${
        mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
      } | Filter Topics - ${val}`,
      click_position: '0',
      nav_link_section: 'Content Filters',
      track_event: 'ui_click',
      event_action: 'ui_click',
      event_type: 'ui_click',
    }

    fireAndForget({
      event_type: track?.event_type,
      ...handleTrackAndReactEvent(event, track),
    })

    setFilterBy(val)
    if (withKeywords) {
      if (withUrl) {
        const filterURl = getFilterUrlByValue(val, jsonContent)
        push(
          `/${getBaseURL(config)}/${filterURl !== '*' ? filterURl : ''}${stringifyQueryParams(
            paramsUtm
          )}`,
          { scroll: false }
        )
      } else {
        push(`/${getBaseURL(config)}/${val !== '*' ? val : ''}${stringifyQueryParams(paramsUtm)}`, {
          scroll: false,
        })
      }
    }
  }
  const isMobile = useViewportSmallerThan('lg')
  const isMedium = useViewportLargerThan('md')

  function ClearFilters() {
    return (
      <>
        <Grid className={'absolute mt-2 mb-0 mx-0 [&_a]:text-neutral-150 [&_a]:cursor-pointer'}>
          <AchieveLink
            style={{ display: 'contents' }}
            noLink
            track={{
              list_name: 'Listing Page Clear Filter',
              event_name: `${
                mainProductConfig[siteEnv || 'achieve']?.['MainName']
              } - Web | ${unslug(fullAsPath)} - `,
              click_id: 'Clear filters',
              click_position: '0',
              click_text: `${
                mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
              } | Clear filters`,
              nav_link_section: 'Content Filters',
              track_event: 'ui_click',
              event_action: 'ui_click',
              event_type: 'ui_click',
            }}
          >
            <Link
              variant="text"
              onClick={() => {
                setFilterBy('*')
                setKeywords('')
                if (withKeywords) {
                  push(`/${getBaseURL(config)}${stringifyQueryParams(paramsUtm)}`, {
                    scroll: false,
                  })
                }
                setKeywordsValue('')
              }}
            >
              Clear filters
            </Link>
          </AchieveLink>
        </Grid>
      </>
    )
  }

  return (
    <>
      <Grid>
        <Grid
          className={classNames(
            'inline items-center justify-around w-full [direction:row]',
            'md:flex md:items-center md:justify-center md:p-0'
          )}
          data-testid="filters-wrapper"
        >
          {withKeywords && (
            <Grid
              className={
                withKeywords
                  ? classNames(
                      '[&_input]:p-[16.8px]',
                      '[&_div_p]:text-neutral-150',
                      'w-full',
                      'md:relative md:w-2/5 md:p-2'
                    )
                  : ''
              }
              data-testid="filter-keywords-wrapper"
            >
              <Typography
                className={classNames('mr-10 my-4', 'bodySm lg:bodyLg')}
                content="Search for keywords"
                id="keywords"
                data-testid="filter-keywords-label"
              />
              <Grid size={{ xs: 4, lg: 4 }}>
                <Box data-testid="filter-keywords-input-wrapper">
                  <form
                    onSubmit={(e) => {
                      e.preventDefault()
                      setKeywords(keywordsValue)
                    }}
                  >
                    <FormControl
                      variant="outlined"
                      className={classNames(
                        '[&>div>div>fieldset]:rounded-[10px]',
                        '[&_div]:rounded-[10px]'
                      )}
                      fullWidth
                    >
                      <TextField
                        onChange={(e) => {
                          setKeywordsValue(e.target.value)
                        }}
                        className={
                          '[&_input]:text-neutral-150 [&_input]:text-lg [&_input::placeholder]:opacity-100 p-0'
                        }
                        variant="outlined"
                        value={keywordsValue}
                        placeholder="Search for keywords"
                      />
                    </FormControl>
                  </form>
                </Box>
              </Grid>
              {(withKeywords && !isMobile) || (isMedium && isMobile) ? ClearFilters() : null}
            </Grid>
          )}
          <Grid
            className={
              withKeywords
                ? classNames(
                    '[&_input]:p-[16.8px]',
                    '[&_div_p]:text-neutral-150',
                    'w-full',
                    'md:relative md:w-2/5 md:p-2'
                  )
                : ''
            }
            data-testid="filter-by-wrapper"
          >
            <Typography
              className={classNames('mr-10 my-4', 'bodySm lg:bodyLg')}
              content={jsonContent.label}
              id="filter-item"
              data-testid="filter-by-label"
            />
            <Grid size={{ xs: 4, lg: 4 }}>
              <Box>
                <FormControl
                  variant="outlined"
                  className={classNames(
                    '[&>div>div>fieldset]:rounded-[10px]',
                    '[&_div]:rounded-[10px]'
                  )}
                  fullWidth
                  data-testid="filter-by-input-wrapper"
                >
                  <noscript>
                    {jsonContent?.options?.map((menuItem, idx) => {
                      return (
                        <a
                          href={
                            menuItem.value == '*'
                              ? `/${getBaseURL(config)}`
                              : `/${getBaseURL(config)}/${withUrl ? menuItem.url : menuItem.value}`
                          }
                          key={idx}
                        >
                          {menuItem.label}
                        </a>
                      )
                    })}
                  </noscript>
                  <Select
                    IconComponent={KeyboardArrowDownIcon}
                    value={filterBy}
                    onChange={handleChange}
                    labelId="filter-item"
                    data-testid="filter-by-select"
                  >
                    {jsonContent.options.map((menuItem, idx) => {
                      return (
                        <MenuItem key={idx} value={menuItem.value}>
                          <Typography content={menuItem.label} className="bodySm lg:bodyLg" />
                        </MenuItem>
                      )
                    })}
                  </Select>
                </FormControl>
              </Box>
            </Grid>
          </Grid>
          {withKeywords && (
            <AchieveLink
              noLink
              track={{
                list_name: 'Listing Page Filter Results',
                event_name: `${
                  mainProductConfig[siteEnv || 'achieve']?.['MainName']
                } - Web | ${unslug(fullAsPath)} -`,
                search_term: keywordsValue,
                click_id: 'Filter Results',
                click_position: '0',
                nav_link_section: 'Content Filters',
                track_event: 'button_click',
                event_action: 'button_click',
                event_type: 'button_click',
              }}
            >
              <AscendButton
                className={classNames('mx-0 my-2', 'md:mt-16 md:mb-[4.8px] md:mx-0')}
                onClick={() => {
                  setKeywords(keywordsValue)
                }}
                variant="contained"
                data-testid="filter-results-button"
              >
                <Typography
                  className={'py-1'}
                  content={isMedium && isMobile ? `Filter` : `Filter Results`}
                  fontWeight="bold"
                  variant="bodyLg"
                />
              </AscendButton>
            </AchieveLink>
          )}
        </Grid>
        {withKeywords && isMobile && !isMedium ? ClearFilters() : null}
      </Grid>
    </>
  )
}

export { Filter }
